/*
 * PENROSE cart drawer — window.PNCart
 *
 *   PNCart.open()                       open the drawer (refreshes first if never loaded)
 *   PNCart.close()
 *   PNCart.toggle(force?)
 *   PNCart.refresh()          -> Promise<cart>   GET /cart.js and re-render
 *   PNCart.add(items, opts)   -> Promise<response of /cart/add.js>
 *        items: [{ id, quantity, properties?, selling_plan? }] (single object also accepted)
 *        opts:  { open: true (default), button: HTMLElement (gets aria-busy) }
 *   PNCart.change(key, quantity) -> Promise<cart>
 *   PNCart.cart                 last cart JSON
 *
 * Events (document):
 *   in : 'pn:cart:open', 'pn:cart:close', 'pn:cart:refresh', 'pn:cart:add' {detail:{items, open}}
 *        'shopify:cart:lines-update' (Horizon product forms / cart), 'cart:update', 'cart:refresh'
 *   out: 'pn:cart:updated' {detail:{cart}}  after every render
 *        'pn:cart:opened' / 'pn:cart:closed'
 * Clicks on [data-pn-cart-open], [aria-controls="cart-drawer"] and links to the cart page open the drawer.
 * Elements with [data-pn-cart-count] get the live item count.
 */
(function () {
  'use strict';
  if (window.PNCart && window.PNCart.__pn) {
    window.PNCart.mount();
    return;
  }

  var ROOT_SEL = '[data-pn-cart]';
  var productCache = {};
  var queued = []; // calls made before the drawer mounted

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function rootUrl() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function url(path) { return rootUrl().replace(/\/$/, '') + path; }

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || '${{amount}}';
    function withDelims(n, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var num = (n / 100).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = '';
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelims(cents, 2, ' ', '.'); break;
      default: value = withDelims(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function imgUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function svg(inner) {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false" class="pn-i">' + inner + '</svg>';
  }

  /* ---------- fetch helpers ---------- */
  function jsonFetch(path, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, opts.headers || {});
    opts.credentials = 'same-origin';
    return fetch(url(path), opts).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || (data && data.status && data.status >= 400)) {
          var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(url('/products/' + handle + '.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  /* ---------- the drawer ---------- */
  var PNCart = {
    __pn: true,
    cart: null,
    root: null,
    cfg: null,
    isOpen: false,
    _loaded: false,
    _reserveTimer: null,
    _lastFocus: null,

    mount: function () {
      var root = $(ROOT_SEL);
      if (!root) return false;
      if (root === this.root) return true;
      this.root = root;
      try { this.cfg = JSON.parse($('[data-pnc-config]', root).textContent); } catch (e) { this.cfg = {}; }
      this.cfg.products = this.cfg.products || {};
      this.icons = this.cfg.icons || {};
      root.hidden = false;
      if (this.isOpen) root.setAttribute('data-state', 'open');
      this._bindRoot(root);
      this._fillMoneyTexts();
      this._startReserve();
      this.refresh();
      var q = queued; queued = [];
      q.forEach(function (fn) { fn(); });
      return true;
    },

    _fillMoneyTexts: function () {
      var self = this;
      $$('[data-pnc-money-text]', this.root).forEach(function (el) {
        var t = el.getAttribute('data-template') || '';
        el.textContent = t.replace('[amount]', self._fmt(self._threshold()));
      });
    },

    _fmt: function (cents) { return formatMoney(cents, this.cfg.moneyFormat); },

    _threshold: function () {
      var t = parseFloat(this.cfg.threshold) || 0;
      var rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
      return Math.round(t * 100 * rate);
    },

    /* ----- open / close ----- */
    open: function () {
      var self = this;
      if (!this.root && !this.mount()) { queued.push(function () { self.open(); }); return; }
      if (!this._loaded) this.refresh();
      this._lastFocus = document.activeElement;
      this.isOpen = true;
      this.root.hidden = false;
      // force reflow so the transition runs when coming from hidden
      void this.root.offsetWidth;
      this.root.setAttribute('data-state', 'open');
      document.documentElement.setAttribute('scroll-lock', '');
      var panel = $('.pnc__panel', this.root);
      setTimeout(function () { if (panel) panel.focus({ preventScroll: true }); }, 60);
      document.dispatchEvent(new CustomEvent('pn:cart:opened'));
    },

    close: function () {
      if (!this.root) return;
      this.isOpen = false;
      this.root.setAttribute('data-state', 'closed');
      var menuOpen = document.querySelector('[data-pn-mm][data-state="open"]');
      if (!menuOpen) document.documentElement.removeAttribute('scroll-lock');
      if (this._lastFocus && this._lastFocus.focus) { try { this._lastFocus.focus({ preventScroll: true }); } catch (e) {} }
      document.dispatchEvent(new CustomEvent('pn:cart:closed'));
    },

    toggle: function (force) {
      var open = typeof force === 'boolean' ? force : !this.isOpen;
      open ? this.open() : this.close();
    },

    /* ----- data ----- */
    refresh: function () {
      var self = this;
      return jsonFetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
        .then(function (cart) { return self._withProducts(cart); })
        .then(function (cart) {
          self.cart = cart;
          self._loaded = true;
          self.render();
          return self._reconcileGifts(cart);
        })
        .catch(function (e) { console.warn('[PNCart] refresh failed', e); return self.cart; });
    },

    add: function (items, opts) {
      var self = this;
      opts = opts || {};
      if (!Array.isArray(items)) items = [items];
      items = items.filter(Boolean).map(function (it) {
        var o = { id: Number(it.id || it.variantId || it.variant_id), quantity: Number(it.quantity || 1) };
        if (it.properties) o.properties = it.properties;
        if (it.selling_plan || it.sellingPlan) o.selling_plan = it.selling_plan || it.sellingPlan;
        return o;
      });
      var btn = opts.button;
      if (btn) btn.setAttribute('aria-busy', 'true');
      return jsonFetch('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: items }) })
        .then(function (res) {
          return self.refresh().then(function () {
            var shouldOpen = opts.open !== undefined ? opts.open : (self.cfg ? self.cfg.openOnAdd !== false : true);
            if (shouldOpen) self.open();
            return res;
          });
        })
        .catch(function (err) {
          console.warn('[PNCart] add failed', err);
          document.dispatchEvent(new CustomEvent('pn:cart:error', { detail: { error: err, items: items } }));
          self.refresh();
          throw err;
        })
        .finally(function () { if (btn) btn.removeAttribute('aria-busy'); });
    },

    change: function (key, quantity) {
      var self = this;
      if (this.root) this.root.setAttribute('aria-busy', 'true');
      return jsonFetch('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }) })
        .then(function () { return self.refresh(); })
        .catch(function (err) { console.warn('[PNCart] change failed', err); return self.refresh(); })
        .finally(function () { if (self.root) self.root.removeAttribute('aria-busy'); });
    },

    _withProducts: function (cart) {
      var map = (this.cfg && this.cfg.products) || {};
      var need = [];
      cart.items.forEach(function (it) {
        var p = map[it.product_id];
        if (!p || !p.compare || p.compare[it.variant_id] === undefined) {
          if (need.indexOf(it.handle) < 0) need.push(it.handle);
        }
      });
      if (!need.length) return Promise.resolve(cart);
      return Promise.all(need.map(fetchProduct)).then(function (products) {
        products.forEach(function (prod) {
          if (!prod) return;
          var entry = map[prod.id] || (map[prod.id] = { inspired: null, compare: {} });
          entry.compare = entry.compare || {};
          prod.variants.forEach(function (v) {
            if (entry.compare[v.id] === undefined) entry.compare[v.id] = v.compare_at_price || 0;
          });
        });
        return cart;
      });
    },

    _isGift: function (it) {
      var p = it.properties || {};
      if (p._pn_gift === '0') return false;
      return !!(p._pn_gift || (it.quantity > 0 && it.final_line_price === 0));
    },

    _compareAt: function (it) {
      var p = this.cfg.products[it.product_id];
      var c = p && p.compare ? parseInt(p.compare[it.variant_id], 10) || 0 : 0;
      return c;
    },

    _giftValue: function (it) {
      var p = it.properties || {};
      var v = parseInt(p._pn_gift_value, 10);
      if (v > 0) return v * it.quantity;
      var cmp = this._compareAt(it) * it.quantity;
      return Math.max(cmp, it.original_line_price || 0);
    },

    _lineOriginal: function (it) {
      var cmp = this._compareAt(it) * it.quantity;
      return Math.max(cmp, it.original_line_price || 0);
    },

    _inspired: function (it) {
      var props = it.properties || {};
      var v = props['Inspired by'] || props._inspired_by || props._pn_inspired;
      if (!v) { var p = this.cfg.products[it.product_id]; v = p && p.inspired; }
      if (!v && it.variant_title && it.variant_title !== 'Default Title' && !it.product_has_only_default_variant) v = it.variant_title;
      return v || '';
    },

    /* ----- gift reconcile: drop gifts whose jar requirement is no longer met ----- */
    _reconcileGifts: function (cart) {
      var self = this;
      if (!this.cfg.giftAutoRemove || this._reconciling) return Promise.resolve(cart);
      var jars = 0;
      cart.items.forEach(function (it) { if (!self._isGift(it)) jars += it.quantity; });
      var fix = null;
      cart.items.some(function (it) {
        var p = it.properties || {};
        if (!p._pn_gift) return false;
        var label = String(p['Free gift'] || p._pn_gift_min || '');
        var m = label.match(/(\d+)/);
        var min = m ? parseInt(m[1], 10) : 1;
        if (jars < min) { fix = { key: it.key, quantity: 0 }; return true; }
        if (it.quantity > 1) { fix = { key: it.key, quantity: 1 }; return true; }
        return false;
      });
      if (!fix) return Promise.resolve(cart);
      this._reconciling = true;
      return jsonFetch('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: fix.key, quantity: fix.quantity }) })
        .catch(function () {})
        .then(function () { self._reconciling = false; return self.refresh(); });
    },

    /* ----- rendering ----- */
    render: function () {
      var cart = this.cart;
      var root = this.root;
      if (!root || !cart) return;
      var self = this;
      var cfg = this.cfg;
      var I = this.icons;
      var empty = cart.item_count === 0;

      $$('[data-pn-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
      var cnt = $('[data-pnc-count]', root); if (cnt) cnt.textContent = cart.item_count;

      var reserve = $('[data-pnc-reserve]', root); if (reserve) reserve.hidden = empty;
      var totals = $('[data-pnc-totals]', root); if (totals) totals.hidden = empty;
      var trust = $('[data-pnc-trust]', root); if (trust) trust.hidden = empty;
      var emptyEl = $('[data-pnc-empty]', root); if (emptyEl) emptyEl.hidden = !empty;

      /* shipping */
      var ship = $('[data-pnc-ship]', root);
      if (ship) {
        var th = this._threshold();
        var total = cart.total_price;
        if (cfg.alwaysFree) {
          ship.setAttribute('data-done', '');
          ship.innerHTML = '<p class="pnc__ship-p pnc__ship-p--done">' + svg(I.done) + '<strong>' + esc(cfg.shipUnlocked) + '</strong></p>' +
            (empty ? '' : '<span class="pnc__bar" role="img" aria-label="100%"><span class="pnc__bar-fill" style="width:100%"></span></span>');
        }
        else if (!th) { ship.innerHTML = ''; }
        else if (empty) {
          ship.removeAttribute('data-done');
          ship.innerHTML = '<p class="pnc__ship-p">' + svg(I.truck) + '<span><strong>' + esc(cfg.shipEmptyBold) + '</strong> ' + esc(String(cfg.shipEmptyRest || '').replace('[amount]', this._fmt(th))) + '</span></p>';
        } else if (total >= th) {
          ship.setAttribute('data-done', '');
          ship.innerHTML = '<p class="pnc__ship-p pnc__ship-p--done">' + svg(I.done) + '<strong>' + esc(cfg.shipUnlocked) + '</strong></p>' +
            '<span class="pnc__bar" role="img" aria-label="100%"><span class="pnc__bar-fill" style="width:100%"></span></span>';
        } else {
          var pct = Math.max(4, Math.min(100, Math.round((total / th) * 100)));
          var txt = esc(String(cfg.shipProgress || '')).replace('[amount]', '<strong>' + esc(this._fmt(th - total)) + '</strong>');
          var prev = $('.pnc__bar-fill', ship);
          var prevW = prev ? prev.style.width : '0%';
          ship.removeAttribute('data-done');
          ship.innerHTML = '<p class="pnc__ship-p">' + svg(I.truck) + '<span>' + txt + '</span></p>' +
            '<span class="pnc__bar" role="img" aria-label="' + pct + '%"><span class="pnc__bar-fill" style="width:' + prevW + '"></span></span>';
          var fill = $('.pnc__bar-fill', ship);
          requestAnimationFrame(function () { requestAnimationFrame(function () { fill.style.width = pct + '%'; }); });
        }
      }

      /* lines: gifts first, then products */
      var gifts = [], lines = [];
      cart.items.forEach(function (it) { (self._isGift(it) ? gifts : lines).push(it); });
      var html = '';
      gifts.forEach(function (it) {
        var p = it.properties || {};
        var note = p._pn_gift_note || String(cfg.giftNote || '').replace('[title]', it.product_title);
        var value = self._giftValue(it);
        var was = p._pn_gift_was || (value > 0 ? self._fmt(value) : '');
        html += '<div class="pnc__gift" data-key="' + esc(it.key) + '">' +
          '<a class="pnc__gift-img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(imgUrl(it.image, 160)) + '" alt="' + esc(it.product_title) + '" width="46" height="46" loading="lazy">' : '') + '</a>' +
          '<div class="pnc__gift-mid"><p class="pnc__gift-t"><span class="pnc__tag">' + svg(I.gift) + esc(cfg.giftTag) + '</span><span class="pnc__gift-name">' + esc(it.product_title) + '</span></p>' +
          (note ? '<p class="pnc__gift-note">' + svg(I.check) + esc(note) + '</p>' : '') + '</div>' +
          '<p class="pnc__gift-p">' + (was ? '<s>' + esc(was) + '</s>' : '') + '<strong>' + esc(cfg.giftFree) + '</strong></p>' +
          '<button type="button" class="pnc__rm" data-pnc-remove="' + esc(it.key) + '" aria-label="' + esc(cfg.removeLabel + ' ' + it.product_title) + '">' + svg(I.x) + '</button>' +
          '</div>';
      });
      lines.forEach(function (it) {
        var insp = self._inspired(it);
        var orig = self._lineOriginal(it);
        var props = it.properties || {};
        var visibleProps = Object.keys(props).filter(function (k) { return k.charAt(0) !== '_' && k !== 'Inspired by' && props[k] !== '' && props[k] != null; })
          .map(function (k) { return esc(k) + ': ' + esc(props[k]); });
        if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) visibleProps.unshift(esc(it.selling_plan_allocation.selling_plan.name));
        html += '<div class="pnc__line" data-key="' + esc(it.key) + '">' +
          '<a class="pnc__img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(imgUrl(it.image, 240)) + '" alt="' + esc(it.product_title) + '" width="68" height="68" loading="lazy">' : '') + '</a>' +
          '<div class="pnc__body">' +
            '<div class="pnc__top"><div class="pnc__top-t">' +
              '<a class="pnc__name" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
              (insp ? '<p class="pnc__insp">' + esc(cfg.inspiredPrefix) + ' <span>' + esc(insp) + '</span></p>' : '') +
              (visibleProps.length ? '<p class="pnc__props">' + visibleProps.join(' \u00b7 ') + '</p>' : '') +
            '</div>' +
            '<button type="button" class="pnc__rm" data-pnc-remove="' + esc(it.key) + '" aria-label="' + esc(cfg.removeLabel + ' ' + it.product_title) + '">' + svg(I.x) + '</button></div>' +
            '<div class="pnc__bottom">' +
              '<div class="pnc__qty">' +
                '<button type="button" data-pnc-minus="' + esc(it.key) + '" aria-label="' + esc(cfg.decreaseLabel) + '">' + svg(I.minus) + '</button>' +
                '<input type="number" inputmode="numeric" min="0" value="' + it.quantity + '" data-pnc-qty="' + esc(it.key) + '" aria-label="' + esc(it.product_title) + '">' +
                '<button type="button" data-pnc-plus="' + esc(it.key) + '" aria-label="' + esc(cfg.increaseLabel) + '">' + svg(I.plus) + '</button>' +
              '</div>' +
              '<div class="pnc__prices">' + (orig > it.final_line_price ? '<s>' + esc(self._fmt(orig)) + '</s>' : '') + '<span>' + esc(self._fmt(it.final_line_price)) + '</span></div>' +
            '</div>' +
          '</div></div>';
      });
      $('[data-pnc-items]', root).innerHTML = html;

      /* summary */
      var original = 0, giftSaved = 0;
      lines.forEach(function (it) { original += self._lineOriginal(it); });
      gifts.forEach(function (it) { var v = self._giftValue(it); original += v; giftSaved += Math.max(0, v - it.final_line_price); });
      var discount = Math.max(0, original - giftSaved - cart.total_price);
      var lineDiscountTitle = null;
      lines.forEach(function (it) {
        if (lineDiscountTitle) return;
        if (it.discounts && it.discounts.length && it.discounts[0].title) lineDiscountTitle = it.discounts[0].title;
      });
      var discountTitle = (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length && cart.cart_level_discount_applications[0].title) || lineDiscountTitle || cfg.discountLabel;
      var sum = '<div class="pnc__row"><dt>' + esc(cfg.originalLabel) + '</dt><dd>' + esc(this._fmt(original)) + '</dd></div>';
      if (discount > 0) sum += '<div class="pnc__row pnc__row--green"><dt>' + esc(discountTitle) + '</dt><dd>\u2212' + esc(this._fmt(discount)) + '</dd></div>';
      if (giftSaved > 0) sum += '<div class="pnc__row pnc__row--green"><dt>' + svg(I.gift) + esc(cfg.giftsLabel) + ' (' + gifts.reduce(function (n, it) { return n + it.quantity; }, 0) + ')</dt><dd>\u2212' + esc(this._fmt(giftSaved)) + '</dd></div>';
      sum += '<div class="pnc__row pnc__row--final"><dt>' + esc(cfg.finalLabel) + '</dt><dd>' + esc(this._fmt(cart.total_price)) + '</dd></div>';
      var sumEl = $('[data-pnc-summary]', root); if (sumEl) sumEl.innerHTML = sum;
      var tc = $('[data-pnc-total-count]', root);
      if (tc) tc.textContent = String(cart.item_count === 1 ? cfg.itemLabel : cfg.itemsLabel).replace('[count]', cart.item_count);
      var tv = $('[data-pnc-total]', root); if (tv) tv.textContent = this._fmt(cart.total_price);

      /* opt-in package protection: reflect cart state, never auto-add */
      var pcb = $('[data-pnc-protect]', root);
      if (pcb) {
        var pid = Number(pcb.getAttribute('data-pnc-protect'));
        var pline = cart.items.filter(function (it) { return it.variant_id === pid; })[0];
        pcb.checked = !!pline;
        pcb._pnKey = pline ? pline.key : null;
        if (!pcb._pnBound) {
          pcb._pnBound = true;
          pcb.addEventListener('change', function () {
            var row = pcb.closest('[data-pnc-protect-row]');
            if (row) row.setAttribute('aria-busy', 'true');
            var done = function () { if (row) row.removeAttribute('aria-busy'); };
            if (pcb.checked) {
              self.add({ id: pid, quantity: 1 }, { open: true }).then(done, done);
            } else if (pcb._pnKey) {
              self.change(pcb._pnKey, 0).then(done, done);
            } else { done(); }
          });
        }
      }

      /* upsell */
      this._renderUpsell();

      document.dispatchEvent(new CustomEvent('pn:cart:updated', { detail: { cart: cart } }));
    },

    _renderUpsell: function () {
      var root = this.root;
      var up = $('[data-pnc-upsell]', root);
      if (!up) return;
      var cart = this.cart;
      var inCart = {};
      cart.items.forEach(function (it) { inCart[it.product_id] = true; });
      var visible = 0;
      $$('.pnc__slide', up).forEach(function (sl) {
        var hide = !!inCart[sl.getAttribute('data-product-id')];
        sl.hidden = hide;
        if (!hide) visible++;
      });
      up.hidden = cart.item_count === 0 || visible === 0;
      var dots = $('[data-pnc-dots]', up);
      var track = $('[data-pnc-track]', up);
      if (dots) {
        var d = '';
        for (var i = 0; i < visible && visible > 1; i++) d += '<button type="button" aria-label="' + (i + 1) + ' / ' + visible + '"' + (i === 0 ? ' aria-current="true"' : '') + ' data-pnc-dot="' + i + '"></button>';
        dots.innerHTML = d;
      }
      if (track) track.scrollLeft = 0;
    },

    _syncDots: function (track) {
      var up = track.closest('[data-pnc-upsell]');
      var slides = $$('.pnc__slide:not([hidden])', track);
      if (!slides.length) return;
      var idx = 0, best = Infinity;
      slides.forEach(function (sl, i) {
        var d = Math.abs(sl.offsetLeft - track.offsetLeft - track.scrollLeft);
        if (d < best) { best = d; idx = i; }
      });
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) idx = slides.length - 1;
      $$('[data-pnc-dot]', up).forEach(function (b, i) { b.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
    },

    /* ----- reserve timer ----- */
    _startReserve: function () {
      var self = this;
      var mins = parseFloat(this.cfg.reserveMinutes) || 5;
      if (!window.__pnReserveEnd || window.__pnReserveEnd < Date.now() - 60000) window.__pnReserveEnd = Date.now() + mins * 60000;
      clearInterval(this._reserveTimer);
      var tick = function () {
        var t = $('[data-pnc-reserve-timer]', self.root);
        var pre = $('[data-pnc-reserve-prefix]', self.root);
        if (!t) return;
        var left = Math.max(0, Math.round((window.__pnReserveEnd - Date.now()) / 1000));
        if (left === 0) { t.textContent = ''; if (pre) pre.textContent = self.cfg.reserveExpired; }
        else {
          var m = Math.floor(left / 60), s = left % 60;
          t.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
          if (pre) pre.textContent = self.cfg.reserveText;
        }
      };
      tick();
      this._reserveTimer = setInterval(tick, 1000);
    },

    /* ----- events inside the drawer ----- */
    _bindRoot: function (root) {
      var self = this;
      root.addEventListener('click', function (e) {
        var t = e.target;
        if (t.closest('[data-pn-cart-close]')) { e.preventDefault(); self.close(); return; }
        var b;
        if ((b = t.closest('[data-pnc-remove]'))) { b.setAttribute('aria-busy', 'true'); self.change(b.getAttribute('data-pnc-remove'), 0); return; }
        if ((b = t.closest('[data-pnc-minus]')) || (b = t.closest('[data-pnc-plus]'))) {
          var key = b.getAttribute('data-pnc-minus') || b.getAttribute('data-pnc-plus');
          var line = self.cart.items.filter(function (it) { return it.key === key; })[0];
          if (!line) return;
          self.change(key, line.quantity + (b.hasAttribute('data-pnc-plus') ? 1 : -1));
          return;
        }
        if ((b = t.closest('[data-pnc-add]'))) {
          e.preventDefault();
          self.add([{ id: b.getAttribute('data-pnc-add'), quantity: 1 }], { button: b, open: true }).catch(function () {});
          return;
        }
        if ((b = t.closest('[data-pnc-dot]'))) {
          var track = $('[data-pnc-track]', root);
          var slides = $$('.pnc__slide:not([hidden])', track);
          var sl = slides[parseInt(b.getAttribute('data-pnc-dot'), 10)];
          if (sl) track.scrollTo({ left: sl.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
      });
      root.addEventListener('change', function (e) {
        var inp = e.target.closest('[data-pnc-qty]');
        if (inp) self.change(inp.getAttribute('data-pnc-qty'), parseInt(inp.value, 10) || 0);
      });
      root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { self.close(); return; }
        if (e.key === 'Tab' && self.isOpen) {
          var f = $$('a[href], button:not([disabled]), input, select', $('.pnc__panel', root)).filter(function (el) { return el.offsetParent !== null; });
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      });
      var track = $('[data-pnc-track]', root);
      if (track) {
        var raf;
        track.addEventListener('scroll', function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(function () { self._syncDots(track); }); }, { passive: true });
        // mouse drag (touch scrolls natively)
        var down = false, startX = 0, startL = 0, moved = false;
        track.addEventListener('mousedown', function (e) { down = true; moved = false; startX = e.pageX; startL = track.scrollLeft; });
        window.addEventListener('mousemove', function (e) {
          if (!down) return;
          var dx = e.pageX - startX;
          if (Math.abs(dx) > 4) { moved = true; track.classList.add('is-drag'); }
          if (moved) track.scrollLeft = startL - dx;
        });
        window.addEventListener('mouseup', function () {
          if (!down) return;
          down = false;
          if (moved) {
            track.classList.remove('is-drag');
            var slides = $$('.pnc__slide:not([hidden])', track);
            var target = slides.reduce(function (best, sl) {
              return Math.abs(sl.offsetLeft - track.offsetLeft - track.scrollLeft) < Math.abs(best.offsetLeft - track.offsetLeft - track.scrollLeft) ? sl : best;
            }, slides[0]);
            if (target) track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: 'smooth' });
          }
        });
        track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      }
    }
  };

  window.PNCart = PNCart;

  /* ---------- global listeners (bound once) ---------- */
  function isCartLink(el) {
    if (!el) return null;
    var hit = el.closest('[data-pn-cart-open], [aria-controls="cart-drawer"]');
    if (hit) return hit;
    var a = el.closest('a[href]');
    if (!a || a.closest(ROOT_SEL)) return null;
    var href = a.getAttribute('href') || '';
    var path = href.split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/, '');
    var cartPath = (PNCart.cfg && PNCart.cfg.cartUrl) || '/cart';
    return path === cartPath || path === '/cart' ? a : null;
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var trigger = isCartLink(e.target);
    if (!trigger) return;
    if (!document.querySelector(ROOT_SEL)) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    PNCart.open();
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && PNCart.isOpen && !(PNCart.root && PNCart.root.contains(e.target))) PNCart.close();
  });
  document.addEventListener('pn:cart:open', function () { PNCart.open(); });
  document.addEventListener('pn:cart:close', function () { PNCart.close(); });
  document.addEventListener('pn:cart:refresh', function () { PNCart.refresh(); });
  document.addEventListener('pn:cart:add', function (e) {
    var d = e.detail || {};
    PNCart.add(d.items || d, { open: d.open !== false, button: d.button }).catch(function () {});
  });

  // Horizon (Shopify standard events): product-form add, cart line changes
  document.addEventListener('shopify:cart:lines-update', function (e) {
    var action = e.action || (e.detail && e.detail.action);
    var p = e.promise || (e.detail && e.detail.promise);
    var done = function () {
      PNCart.refresh().then(function () {
        if (action === 'add' && (!PNCart.cfg || PNCart.cfg.openOnAdd !== false)) PNCart.open();
      });
    };
    if (p && typeof p.then === 'function') p.then(done, function () { PNCart.refresh(); });
    else setTimeout(done, 400);
  });
  ['cart:update', 'cart:refresh', 'cart-section:restored'].forEach(function (name) {
    document.addEventListener(name, function () { PNCart.refresh(); });
  });

  // back/forward cache
  window.addEventListener('pageshow', function (e) { if (e.persisted) PNCart.refresh(); });

  // theme editor
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector(ROOT_SEL)) { PNCart.root = null; PNCart.mount(); }
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector(ROOT_SEL)) PNCart.open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector(ROOT_SEL)) PNCart.close();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { PNCart.mount(); });
  else PNCart.mount();
})();
